import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, OnDestroy, inject, isDevMode } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { composeAnalyticsValue, resolveAnalyticsPage, sanitizeAnalyticsName } from '../directives/app-analytics.utils';

const DATA_ANALYTICS_ATTRIBUTE = 'data-analytics';
const OPT_OUT_ATTRIBUTE = 'data-analytics-off';
const HYDRATION_SELECTOR =
  `button:not([${DATA_ANALYTICS_ATTRIBUTE}]):not([${OPT_OUT_ATTRIBUTE}]), ` +
  `a:not([${DATA_ANALYTICS_ATTRIBUTE}]):not([${OPT_OUT_ATTRIBUTE}])`;
const STAMPED_SELECTOR = `button[${DATA_ANALYTICS_ATTRIBUTE}], a[${DATA_ANALYTICS_ATTRIBUTE}]`;
const DENYLIST_ANCESTOR_SELECTOR = 'mat-calendar, mat-datepicker-content, mat-paginator';
const MENU_ITEM_SELECTOR = '[role="menuitem"], [mat-menu-item], .mat-mdc-menu-item, .mat-menu-item';
const REPEATED_ROW_SELECTOR = 'tr, mat-row, [role="row"]';
const MAX_NAME_LENGTH = 40;

interface AppAnalyticsDumpEntry {
  value: string;
  element: Element;
}

interface AppAnalyticsReport {
  stamped: AppAnalyticsDumpEntry[];
  unresolved: Element[];
  suppressed: Element[];
  duplicates: Record<string, Element[]>;
}

@Injectable({ providedIn: 'root' })
export class AppAnalyticsHydratorService implements OnDestroy {
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  private observer?: MutationObserver;
  private navigationSubscription?: Subscription;
  private pendingRestamp?: ReturnType<typeof setTimeout>;
  private deferring = false;
  private owned = new WeakMap<Element, string>();

  start(): void {
    if (this.observer) {
      return;
    }
    this.hydrate(this.document.body);
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver((mutations) => this.handleMutations(mutations));
      this.observer.observe(this.document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        // The DOM lowercases attribute names, so a `[attr.appAnalytics]` binding renders as `appanalytics`.
        attributeFilter: ['appanalytics'],
      });
    });
    this.navigationSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.ngZone.runOutsideAngular(() => this.scheduleDeferredRescan()));
    this.registerDumpApi();
  }

  hydrate(root: Element, page = resolveAnalyticsPage(this.router)): void {
    if (root.matches(HYDRATION_SELECTOR)) {
      this.applyValue(root, page);
    }
    root.querySelectorAll(HYDRATION_SELECTOR).forEach((element) => this.applyValue(element, page));
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.navigationSubscription?.unsubscribe();
    clearTimeout(this.pendingRestamp);
  }

  private handleMutations(mutations: MutationRecord[]): void {
    const page = resolveAnalyticsPage(this.router);
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof Element) {
          this.handleNameOverrideChange(mutation.target, page);
        }
        return;
      }
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          this.hydrate(node, page);
        }
      });
    });
  }

  private handleNameOverrideChange(element: Element, page: string): void {
    if (this.owned.has(element) || element.matches(HYDRATION_SELECTOR)) {
      this.applyValue(element, page);
    }
  }

  private scheduleDeferredRescan(): void {
    // Bound labels aren't in the DOM yet when this is scheduled: NavigationEnd precedes the router view's
    // first change detection, and freshly-attached content (dialogs) fills interpolations one CD later.
    // Defer one macrotask so names have rendered before we rescan.
    clearTimeout(this.pendingRestamp);
    this.pendingRestamp = setTimeout(() => {
      this.deferring = true;
      const page = resolveAnalyticsPage(this.router);
      const pagePrefix = `${page}-`;
      Array.from(this.document.body.querySelectorAll(STAMPED_SELECTOR))
        .filter((element) => this.owned.has(element))
        .filter((element) => !(element.getAttribute(DATA_ANALYTICS_ATTRIBUTE) ?? '').startsWith(pagePrefix))
        .forEach((element) => this.applyValue(element, page));
      this.hydrate(this.document.body, page);
      this.reportInDevMode();
      this.deferring = false;
    });
  }

  private applyValue(element: Element, page = resolveAnalyticsPage(this.router)): void {
    if (element.closest(DENYLIST_ANCESTOR_SELECTOR)) {
      return;
    }
    const current = element.getAttribute(DATA_ANALYTICS_ATTRIBUTE);
    const lastWritten = this.owned.get(element);
    if (current !== null && current !== lastWritten) {
      // A value we didn't write is present — a manual attribute, or a binding that took over after we
      // stamped. Manual/bound values always win, so the write path never overwrites one.
      return;
    }
    const name = this.resolveName(element);
    if (!name) {
      // A control whose label hasn't rendered yet (e.g. dialog content attached before its first change
      // detection). Retry past the next render instead of dropping it; the guard stops a rescan loop.
      if (!this.deferring && !this.owned.has(element)) {
        this.scheduleDeferredRescan();
      }
      return;
    }
    if (this.isLowQualityName(name)) {
      return;
    }
    const value = composeAnalyticsValue(page, name, this.deriveSuffix(element));
    if (!value) {
      return;
    }
    if (current !== value) {
      element.setAttribute(DATA_ANALYTICS_ATTRIBUTE, value);
    }
    this.owned.set(element, value);
  }

  private resolveName(element: Element): string {
    const explicit = sanitizeAnalyticsName(element.getAttribute('appAnalytics'));
    if (explicit) {
      return explicit;
    }

    const ariaLabel = sanitizeAnalyticsName(element.getAttribute('aria-label'));
    if (ariaLabel) {
      return ariaLabel;
    }

    const tooltip = sanitizeAnalyticsName(element.getAttribute('matTooltip'));
    if (tooltip) {
      return tooltip;
    }

    const visibleText = sanitizeAnalyticsName(this.getVisibleText(element));
    if (visibleText) {
      return visibleText;
    }

    return sanitizeAnalyticsName(element.querySelector('mat-icon')?.textContent);
  }

  private getVisibleText(element: Element): string {
    let text = '';
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent ?? '';
      } else if (node instanceof Element && node.tagName.toLowerCase() !== 'mat-icon') {
        text += this.getVisibleText(node);
      }
    });
    return text;
  }

  private deriveSuffix(element: Element): string {
    return element.matches(MENU_ITEM_SELECTOR) ? '-menu-item' : '-button';
  }

  private isLowQualityName(name: string): boolean {
    return name.length > MAX_NAME_LENGTH || /^[\d-]+$/.test(name);
  }

  private buildReport(): AppAnalyticsReport {
    const stamped = Array.from(this.document.body.querySelectorAll(STAMPED_SELECTOR))
      .filter((element) => this.owned.has(element))
      .map((element) => ({ value: element.getAttribute(DATA_ANALYTICS_ATTRIBUTE) ?? '', element }));

    const unresolved: Element[] = [];
    const suppressed: Element[] = [];
    this.document.body.querySelectorAll(HYDRATION_SELECTOR).forEach((element) => {
      if (element.closest(DENYLIST_ANCESTOR_SELECTOR)) {
        suppressed.push(element);
        return;
      }
      const name = this.resolveName(element);
      if (!name) {
        unresolved.push(element);
      } else if (this.isLowQualityName(name)) {
        suppressed.push(element);
      }
    });

    return { stamped, unresolved, suppressed, duplicates: this.collectDuplicates(stamped) };
  }

  private collectDuplicates(stamped: AppAnalyticsDumpEntry[]): Record<string, Element[]> {
    const byValue = new Map<string, Element[]>();
    stamped.forEach((entry) => {
      const group = byValue.get(entry.value) ?? [];
      group.push(entry.element);
      byValue.set(entry.value, group);
    });
    const duplicates: Record<string, Element[]> = {};
    byValue.forEach((elements, value) => {
      if (elements.length > 1 && !this.isPerRowGroup(elements)) {
        duplicates[value] = elements;
      }
    });
    return duplicates;
  }

  private isPerRowGroup(elements: Element[]): boolean {
    const rows = elements.map((element) => element.closest(REPEATED_ROW_SELECTOR));
    return rows.every(Boolean) && new Set(rows).size === elements.length;
  }

  private reportInDevMode(): void {
    if (!isDevMode()) {
      return;
    }
    const report = this.buildReport();
    const duplicateCount = Object.keys(report.duplicates).length;
    if (!report.unresolved.length && !duplicateCount) {
      return;
    }
    console.groupCollapsed(
      `[appAnalytics] ${report.stamped.length} stamped, ${report.unresolved.length} unresolved, ` +
        `${report.suppressed.length} suppressed, ${duplicateCount} duplicated`,
    );
    if (report.unresolved.length) {
      console.info('Unresolved (add an aria-label or appAnalytics):', report.unresolved);
    }
    if (duplicateCount) {
      console.info('Duplicated values:', report.duplicates);
    }
    console.groupEnd();
  }

  private registerDumpApi(): void {
    if (!isDevMode()) {
      return;
    }
    const view = this.document.defaultView as (Window & { __appAnalyticsDump?: () => AppAnalyticsReport }) | null;
    if (!view) {
      return;
    }
    view.__appAnalyticsDump = (): AppAnalyticsReport => this.buildReport();
  }
}
